import { campaignTypes, campaignWidgetStyle, serverCampaignTypes } from 'config/constants';

export const productSelection = [
  'all_store',
  'collection',
  'tag',
  'vendor',
  'price_range',
  'specific_product',
  'product_type',
  'variant_metafield',
];

export const productExclude = [
  'none',
  'collection',
  'tag',
  'vendor',
  'price_range',
  'specific_product',
  'product_type',
  'variant_metafield',
];

const generalDiscountType = ['new_price', 'percentage', 'fix_price', 'new_price'];

// mapProducts expects p.variantIds (array of numeric strings). A row fresh
// off ResourcePickerModal (mapProductstoSave in utils/mapData.js) carries
// that same list under variantIdList instead — mapProducts falls back to
// re-deriving variantId from p.variants when variantIds is absent, but that
// fallback walks EVERY variant on the product rather than just the ones the
// merchant actually picked, and was the reason a freshly-picked "Products &
// variants" scope resolved to 0 in Coverage (and, unnoticed until now, on a
// real save too — this same normalization was already applied to the BOGO
// paths below, just never to the general scope-of-discount ones). Always
// normalize before calling mapProducts, not just for BOGO.
//
// Only rewrite variantIds when variantIdList actually has something — a row
// loaded from an EXISTING saved campaign (get.adaptor.js's
// ProductAdaptorForLoad) has neither variantIds NOR variantIdList, just one
// real variantId + a single-entry variants array (its shape is already
// per-variant, one row per variant, not grouped). Forcing variantIds to []
// for that row (the previous version of this function did, unconditionally)
// made mapProducts take its variantIds-is-an-empty-array branch instead of
// falling through to its own p.variants handling — silently producing ZERO
// output rows for every loaded campaign's products, not just freshly-picked
// ones. Confirmed live: every existing campaign's Coverage preview read
// "0 of 0" because of this. Leaving the row untouched when there's no
// variantIdList lets mapProducts's own existing fallback do its job.
export function normalizeProductRows(products) {
  return (products ?? []).map((p) => {
    if (p.variantIds?.length || !p.variantIdList?.length) return p;
    return { ...p, variantIds: p.variantIdList };
  });
}

const quantityDiscountType = ['percentage', 'fix_price', 'new_price'];

const generalDiscountMode = [
  'price_discounted_into_compare',
  'compare_discounted_into_price',
  'only_price_discounted',
  'both_price_and_compare_discounted',
];

const volumeMinType = ['quantity', 'amount'];

const volumeMinApplyType = ['all_items', 'each_item'];

// "--1", "--2" are placeholders for variants whose title was unknown at load
// time; never persist them into meta.variants.
const scrubVariantTitle = (v) =>
  v && typeof v.title === 'string' && /^--\d+$/.test(v.title.trim()) ? { ...v, title: '' } : v;

export function mapProducts(products) {
  const variants = [];
  products.forEach((p) => {
    p.variantIds?.forEach((v) => {
      // Store ONLY this row's variant in meta (not the whole product array).
      // Excluded variants resolve their title from meta on the backend, so each
      // row's meta must contain its own variant — otherwise it shows "--1".
      const matchedVariant = p.variants?.find(
        (vv) => String(vv.id) === String(v)
          || String(vv.id) === `gid://shopify/ProductVariant/${v}`,
      );
      const variant = {
        productId: parseInt(p.productId),
        variantId: parseInt(v),
        isExcluded: p.isExclude,
        isAutomatic:p.isAutomatic,
        conflictedCampaignId:p.conflictedCampaignId ? p.conflictedCampaignId : null,
        meta: {
          title: p.title || p.product?.title,
          handle: p.handle || p.product?.handle,
          images: [{ originalSrc: p.image?.src || p.product?.image }],
          variants: matchedVariant ? [scrubVariantTitle(matchedVariant)] : p.variants?.map(scrubVariantTitle),
        },
      };
      var foundeddouplicate = variants.findIndex(
        (item) => item.variantId === variant.variantId && item.isExcluded === p.isExcluded
      );
      if (foundeddouplicate == -1) {
        variants.push(variant);
      }
    });
    p.variantIds==undefined? p.variants?.forEach((v) => {
      const variant = {
        productId: parseInt(p.productId),
        variantId: parseInt(v.id),
        isExcluded: p.isExclude,
        isAutomatic:p.isAutomatic,
        conflictedCampaignId:p.conflictedCampaignId ? p.conflictedCampaignId : null,
        meta: {
          title: p.title || p.product?.title,
          handle: p.handle || p.product?.handle,
          images: [{ originalSrc: p.image?.src || p.product?.image }],
          // `v` is this row's variant object — store just it, not the whole array.
          variants: [scrubVariantTitle(v)],
        },
      };
      var foundeddouplicate = variants.findIndex(
        (item) => item.variantId === variant.variantId && item.isExcluded === p.isExcluded
      );
      if (foundeddouplicate == -1) {
        variants.push(variant);
      }
    }):p.variantIds?.forEach((v) => {});
  });
  return variants;
}

// ── Product Bundle ──────────────────────────────────────────────────────────
// Slot rows keep the picker shapes (auctionProducts / auctionCollections /
// auctionTags / auctionVendors / auctionProductTypes, see bundleConstants.js
// newSlot). The DTO wants ids per selection type plus display meta for
// specific products (built with the same mapProducts/normalizeProductRows the
// BOGO buyProducts use) and collections.
const BUNDLE_SLOT_ID_KEY = {
  specific_product: 'productIds',
  collection: 'collectionIds',
  tag: 'tags',
  vendor: 'vendors',
  product_type: 'productTypes',
};

const digitsOnly = (v) => String(v ?? '').replace(/\D/g, '');
const includeRows = (list) => (Array.isArray(list) ? list : []).filter((x) => x && !x.isExclude);

const bundleSlotIds = (slot) => {
  switch (slot?.selectionType) {
    case 'collection':
      return includeRows(slot.auctionCollections).map((c) => String(c.collectionId));
    case 'tag':
      return includeRows(slot.auctionTags).map((t) => t.tagName);
    case 'vendor':
      return includeRows(slot.auctionVendors).map((v) => v.vendorId ?? v.vendorName);
    case 'product_type':
      return includeRows(slot.auctionProductTypes).map((t) => t.productTypeName);
    default: {
      const ids = [];
      includeRows(slot?.auctionProducts).forEach((p) => {
        const id = digitsOnly(p.productId);
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids;
    }
  }
};

// Flatten a mapProducts() row to the bundle slot `products` display meta.
const bundleProductMeta = (v) => {
  const variant = v?.meta?.variants?.[0];
  const price = variant?.price;
  return {
    productId: String(v.productId),
    variantId: String(v.variantId),
    title: v?.meta?.title ?? '',
    variantTitle: variant?.title && variant.title !== 'Default Title' ? variant.title : '',
    image: v?.meta?.images?.[0]?.originalSrc ?? null,
    price: price != null && price !== '' ? Number(typeof price === 'object' ? price.amount : price) : null,
    handle: v?.meta?.handle ?? '',
  };
};

// Also used by the live slot-coverage preview (bundle/useBundleSlotCoverage.js)
// so the preview and the save send exactly the same slot DTO.
export function bundleSlotToDto(slot, index) {
  const selectionType = slot?.selectionType || 'specific_product';
  const idKey = BUNDLE_SLOT_ID_KEY[selectionType] || 'productIds';
  const dto = {
    position: index,
    title: slot?.title || '',
    selectionType,
    [idKey]: bundleSlotIds(slot),
    requiredQty: Math.max(1, Number(slot?.requiredQty) || 1),
    maxQty:
      slot?.maxQty !== '' && slot?.maxQty != null && Number(slot.maxQty) > 0
        ? Number(slot.maxQty)
        : null,
    isOptional: Boolean(slot?.isOptional),
    autoUpdate: selectionType !== 'specific_product',
    excludeProductIds: (slot?.excludeProducts ?? []).map((p) => digitsOnly(p.productId)).filter(Boolean),
    variantOptionValues: Array.isArray(slot?.variantOptionValues) ? slot.variantOptionValues : [],
  };
  if (selectionType === 'specific_product') {
    dto.products = mapProducts(normalizeProductRows(includeRows(slot?.auctionProducts))).map(
      bundleProductMeta
    );
    // Exactly the variant ids actually checked in the resource picker (one
    // dto.products row per picked variant, same shape every other campaign
    // type's picker already produces) — narrows the slot to just these
    // variants instead of "every variant of productIds". A merchant who
    // never opened the variant checkboxes still ends up with every variant
    // of the product here, since Shopify's picker pre-selects them all —
    // same effective result as before this field existed.
    dto.variantIds = dto.products.map((p) => p.variantId).filter(Boolean);
  }
  if (selectionType === 'collection') {
    dto.collections = includeRows(slot?.auctionCollections).map((c) => ({
      collectionId: String(c.collectionId),
      title: c.title || c.collection?.title || '',
      image: c.image || c.collection?.image || null,
    }));
  }
  return dto;
}

const TIERABLE_BUNDLE_REWARDS = ['percentage', 'fixed_amount', 'fixed_set_price'];

// F4.1 — "Publish as a product (Combo)". Matches the backend's BundleComboDto
// one to one: { enabled, title, priceMode, price, priceSync,
// consolidatedOptions, showContents, productId }. `bundleTitle` is the
// bundle's own top-level title (cfg.title), used as the combo title fallback
// per the F4.1 brief ("combo product title, defaults to the bundle's own
// title") — the merchant's own explicit combo.title always wins.
// Rule 2 (addendum §6): once productId is set the product is real and
// permanent — that id round-trips untouched, never cleared or reassigned
// from here, and the UI (BundleComboCard) never offers a way to unset it.
export function bundleComboToDto(combo, bundleTitle) {
  const enabled = !!combo?.enabled;
  const priceMode = combo?.priceMode === 'computed' ? 'computed' : 'fixed';
  return {
    enabled,
    title: (combo?.title || bundleTitle || '').trim(),
    priceMode,
    price: priceMode === 'fixed' && combo?.price !== '' && combo?.price != null
      ? Number(combo.price)
      : null,
    // Opt-in, default OFF (addendum rule 3) — only meaningful in Computed mode.
    priceSync: priceMode === 'computed' ? !!combo?.priceSync : false,
    consolidatedOptions: !!combo?.consolidatedOptions,
    showContents: combo?.showContents !== false,
    productId: combo?.productId ?? null,
  };
}

export function bundleConfigToDto(cfg) {
  if (!cfg) return null;
  const reward = cfg.reward ?? {};
  const rewardType = reward.type || 'percentage';
  const isRepeat = cfg.applicationMode === 'REPEAT_PER_SET';
  const usesTiers = !!cfg.tiersEnabled && isRepeat && TIERABLE_BUNDLE_REWARDS.includes(rewardType);
  const freeGifts =
    rewardType === 'free_gift'
      ? mapProducts(normalizeProductRows(includeRows(reward.freeGifts))).map((v) => {
          const meta = bundleProductMeta(v);
          return {
            productId: meta.productId,
            variantId: meta.variantId,
            qty: 1,
            meta: { title: meta.title, image: meta.image, price: meta.price, handle: meta.handle },
          };
        })
      : [];
  return {
    title: cfg.title || '',
    note: cfg.note || '',
    slots: (cfg.slots ?? []).map(bundleSlotToDto),
    reward: {
      type: rewardType,
      value: TIERABLE_BUNDLE_REWARDS.includes(rewardType) ? Number(reward.value) || 0 : 0,
      freeGifts,
    },
    applicationMode: isRepeat ? 'REPEAT_PER_SET' : 'ONCE_PER_ORDER',
    rewardAllocation: cfg.rewardAllocation === 'most_expensive' ? 'most_expensive' : 'cheapest',
    maxSetsPerOrder:
      isRepeat && cfg.maxSetsPerOrder !== '' && cfg.maxSetsPerOrder != null
        ? Math.max(1, Number(cfg.maxSetsPerOrder) || 1)
        : null,
    setTiers: usesTiers
      ? (cfg.setTiers ?? [])
          .map((t) => ({ minSets: Number(t.minSets) || 0, value: Number(t.value) || 0 }))
          .sort((a, b) => a.minSets - b.minSets)
      : [],
    combineMode: cfg.combineMode === 'stack' ? 'stack' : 'best_wins',
    executionOrder: 'AFTER_OTHER_DISCOUNTS',
    storefrontDisplay: cfg.storefrontDisplay || 'BOTH',
    storefrontLayout: cfg.storefrontLayout || 'AUTO',
    combo: bundleComboToDto(cfg.combo, cfg.title),
  };
}

// ── Cross-sell ──────────────────────────────────────────────────────────────
// Trigger/offer rows keep the picker shapes (auctionProducts / auctionCollections
// / auctionTags / auctionVendors / auctionProductTypes, see xsellConstants.js
// newTrigger/newOffer). Unlike a bundle slot, the trigger DTO always uses a
// single `ids` field regardless of type, and the offer DTO splits manual
// picks (`productIds`) from a pool's matching rule (`poolType` + `poolIds`) —
// see the plan's xsell-config.dto.ts contract (§4.2).
const xsellTriggerIds = (trigger) => {
  switch (trigger?.type) {
    case 'collection':
      return includeRows(trigger.auctionCollections).map((c) => String(c.collectionId));
    case 'tag':
      return includeRows(trigger.auctionTags).map((t) => t.tagName);
    case 'vendor':
      return includeRows(trigger.auctionVendors).map((v) => v.vendorId ?? v.vendorName);
    case 'product_type':
      return includeRows(trigger.auctionProductTypes).map((t) => t.productTypeName);
    case 'specific_product': {
      const ids = [];
      includeRows(trigger?.auctionProducts).forEach((p) => {
        const id = digitsOnly(p.productId);
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids;
    }
    default:
      return [];
  }
};

const xsellOfferPoolIds = (offer) => {
  switch (offer?.poolType) {
    case 'collection':
      return includeRows(offer.auctionCollections).map((c) => String(c.collectionId));
    case 'tag':
      return includeRows(offer.auctionTags).map((t) => t.tagName);
    case 'vendor':
      return includeRows(offer.auctionVendors).map((v) => v.vendorId ?? v.vendorName);
    case 'product_type':
      return includeRows(offer.auctionProductTypes).map((t) => t.productTypeName);
    default:
      return [];
  }
};

const xsellOfferManualIds = (offer) => {
  const ids = [];
  includeRows(offer?.auctionProducts).forEach((p) => {
    const id = digitsOnly(p.productId);
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
};

// Also used by the live offer-preview hook (xsell/useXsellOfferPreview.js) so
// the preview and the save send exactly the same trigger/offer shape.
// Picker display rows per picked variant (title/handle/image/price) — the
// backend stores them as round-trip meta so the builder reloads the same picks.
const pickerRowsMeta = (list) => mapProducts(normalizeProductRows(includeRows(list))).map(bundleProductMeta);

export function xsellTriggerToDto(trigger) {
  return {
    type: trigger?.type || 'any',
    ids: trigger?.type === 'any' ? [] : xsellTriggerIds(trigger),
    products: trigger?.type === 'specific_product' ? pickerRowsMeta(trigger.auctionProducts) : [],
    minQty: Math.max(1, Number(trigger?.minQty) || 1),
    minSpend:
      trigger?.minSpend !== '' && trigger?.minSpend != null && Number(trigger.minSpend) > 0
        ? Number(trigger.minSpend)
        : null,
  };
}

export function xsellOfferToDto(offer) {
  const source = offer?.source || 'manual';
  const manualIds = source === 'manual' ? xsellOfferManualIds(offer).slice(0, 6) : [];
  return {
    source,
    productIds: manualIds,
    products: source === 'manual' ? pickerRowsMeta(offer?.auctionProducts).slice(0, 6) : [],
    poolType: source === 'pool' ? offer?.poolType || null : null,
    poolIds: source === 'pool' ? xsellOfferPoolIds(offer) : [],
    exclusions: (offer?.excludeProducts ?? []).map((p) => digitsOnly(p.productId)).filter(Boolean),
    pinnedProductIds: Array.isArray(offer?.pinnedProductIds) ? offer.pinnedProductIds : [],
    approvedProductIds: Array.isArray(offer?.approvedProductIds) ? offer.approvedProductIds : [],
    // Manual mode has no separate "top N" control — every picked product
    // shows, so displayCount mirrors the picker's own 1-6 cap instead of a
    // stale default. Pool/smart use the merchant's own displayCount field.
    displayCount:
      source === 'manual'
        ? Math.max(1, manualIds.length || 1)
        : Math.max(1, Math.min(6, Number(offer?.displayCount) || 3)),
  };
}

const XSELL_REWARD_TYPES = ['none', 'percentage', 'fixed', 'new_price'];

export function xsellConfigToDto(cfg) {
  if (!cfg) return null;
  const reward = cfg.reward ?? {};
  const rewardType = XSELL_REWARD_TYPES.includes(reward.type) ? reward.type : 'none';
  return {
    title: cfg.title || '',
    trigger: xsellTriggerToDto(cfg.trigger),
    offer: xsellOfferToDto(cfg.offer),
    reward:
      rewardType === 'none'
        ? { type: 'none' }
        : {
            type: rewardType,
            value: Number(reward.value) || 0,
            maxQty: Math.max(1, Number(reward.maxQty) || 1),
            applyMode: reward.applyMode === 'widget_only' ? 'widget_only' : 'auto',
          },
    combineMode: cfg.combineMode === 'stack' ? 'stack' : 'best_wins',
    placement: cfg.placement || 'BOTH',
  };
}

// ── Upsell (Upgrade offer) ──────────────────────────────────────────────────
// Trigger/offer rows keep the same picker shapes as xsell's trigger/offer
// (upsellConstants.js newUpsellTrigger/newUpsellOffer) and convert with the
// SAME includeRows + digitsOnly id extraction xsell uses. Contract:
// docs/Bundle architect/upsell-implementation-plan.md §2.1 — v1 trigger is
// specific products only (bare ids), the offer splits manual picks
// (`productIds`) from a pool rule (`poolType` + `poolIds`), and the variant
// ladder is [{productId, option}] (only in variant mode).
const upsellTriggerIds = (trigger) => {
  const ids = [];
  includeRows(trigger?.auctionProducts).forEach((p) => {
    const id = digitsOnly(p.productId);
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
};

// Also used by the live builder hooks (upsell/useUpsellOfferPreview.js,
// useUpsellLadderAxes.js) so the preview and the save send exactly the same
// trigger/offer shape.
export function upsellTriggerToDto(trigger) {
  return { productIds: upsellTriggerIds(trigger), products: pickerRowsMeta(trigger?.auctionProducts) };
}

export function upsellOfferToDto(offer) {
  const source = ['manual', 'pool', 'smart'].includes(offer?.source) ? offer.source : 'manual';
  const manualIds = source === 'manual' ? xsellOfferManualIds(offer).slice(0, 6) : [];
  return {
    source,
    productIds: manualIds,
    products: source === 'manual' ? pickerRowsMeta(offer?.auctionProducts).slice(0, 6) : [],
    poolType: source === 'pool' ? offer?.poolType || null : null,
    poolIds: source === 'pool' ? xsellOfferPoolIds(offer) : [],
  };
}

const UPSELL_MODES_LIST = ['product_upgrade', 'variant_upgrade'];
const UPSELL_INCENTIVE_TYPES = ['none', 'percentage', 'fixed', 'max_diff'];

export function upsellConfigToDto(cfg) {
  if (!cfg) return null;
  const mode = UPSELL_MODES_LIST.includes(cfg.mode) ? cfg.mode : 'product_upgrade';
  const trigger = upsellTriggerToDto(cfg.trigger);
  const incentive = cfg.incentive ?? {};
  const incentiveType = UPSELL_INCENTIVE_TYPES.includes(incentive.type) ? incentive.type : 'none';
  // Only ladder entries for products still in the trigger, one per product.
  const ladder =
    mode === 'variant_upgrade'
      ? (Array.isArray(cfg.ladder) ? cfg.ladder : [])
          .map((e) => ({ productId: digitsOnly(e?.productId), option: String(e?.option ?? '').trim() }))
          .filter((e, i, arr) => e.productId && e.option && trigger.productIds.includes(e.productId)
            && arr.findIndex((x) => x.productId === e.productId) === i)
      : [];
  return {
    mode,
    trigger,
    ladder,
    // Variant mode has no offer list — the ladder IS the offer. Send an empty
    // manual offer so the DTO shape stays constant for the backend validator.
    offer: mode === 'variant_upgrade'
      ? { source: 'manual', productIds: [], poolType: null, poolIds: [] }
      : upsellOfferToDto(cfg.offer),
    incentive: {
      type: incentiveType,
      value: incentiveType === 'none' ? null : Number(incentive.value) || 0,
    },
    combineMode: cfg.combineMode === 'stack' ? 'stack' : 'best_wins',
    // '' = use the widget-settings default for the locale (plan D8).
    phrasing: String(cfg.phrasing ?? '').trim().slice(0, 200),
    placement: ['NONE', 'PRODUCT_PAGE', 'CART', 'BOTH'].includes(cfg.placement) ? cfg.placement : 'BOTH',
  };
}

// ── Free Gift ────────────────────────────────────────────────────────────────
// Scope rows keep the same picker shapes as xsell's trigger (see
// giftConstants.js's newGiftScope — the gift-engine spec calls it "scope" not
// "trigger", but the shape is identical); each tier's `giftVariants` rows
// mirror how Bundle's `reward.freeGifts` picker rows convert (mapProducts +
// normalizeProductRows, qty hardcoded to 1 — the picker itself has no
// per-row qty control, same simplification Bundle's free-gift reward uses).
const giftScopeIds = (scope) => {
  switch (scope?.type) {
    case 'collection':
      return includeRows(scope.auctionCollections).map((c) => String(c.collectionId));
    case 'tag':
      return includeRows(scope.auctionTags).map((t) => t.tagName);
    case 'vendor':
      return includeRows(scope.auctionVendors).map((v) => v.vendorId ?? v.vendorName);
    case 'product_type':
      return includeRows(scope.auctionProductTypes).map((t) => t.productTypeName);
    case 'specific_product': {
      const ids = [];
      includeRows(scope?.auctionProducts).forEach((p) => {
        const id = digitsOnly(p.productId);
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids;
    }
    default:
      return [];
  }
};

// Also used by a future live scope-pool preview (see
// gift/useGiftScopePreview.js, not built in v1 — no coverage/offer-preview
// equivalent is spec'd for gift yet) so a preview and the save would send
// exactly the same scope shape, same convention as
// bundleSlotToDto/xsellTriggerToDto above.
export function giftScopeToDto(scope) {
  const base = {
    type: scope?.type || 'any',
    ids: scope?.type === 'any' ? [] : giftScopeIds(scope),
  };
  if (scope?.type !== 'specific_product') return base;
  // Picker rows per picked variant (same shape Bundle slots send) so the builder
  // reloads exactly what was picked, and variantIds narrows the scope server-side.
  const products = mapProducts(normalizeProductRows(includeRows(scope?.auctionProducts))).map(bundleProductMeta);
  return { ...base, products, variantIds: products.map((p) => p.variantId).filter(Boolean) };
}

function giftTierVariantsToDto(giftVariants) {
  return mapProducts(normalizeProductRows(includeRows(giftVariants))).map((v) => {
    const meta = bundleProductMeta(v);
    return {
      productId: meta.productId,
      variantId: meta.variantId,
      qty: 1,
      meta: { title: meta.title, image: meta.image, price: meta.price, handle: meta.handle },
    };
  });
}

const GIFT_TIER_MODES = ['auto_add', 'customer_picks'];
const GIFT_DISPLAY_MODES = ['BAR', 'TOAST', 'SILENT'];

export function giftTierToDto(tier) {
  const mode = GIFT_TIER_MODES.includes(tier?.mode) ? tier.mode : 'auto_add';
  const giftVariants = giftTierVariantsToDto(tier?.giftVariants);
  return {
    minSpend: tier?.minSpend !== '' && tier?.minSpend != null ? Number(tier.minSpend) : 0,
    mode,
    // `picks` is only meaningful in customer_picks mode — null otherwise,
    // matching the CONTRACT's example payload.
    picks: mode === 'customer_picks' && tier?.picks !== '' && tier?.picks != null
      ? Math.max(1, Number(tier.picks) || 1)
      : null,
    giftVariants,
  };
}

// NOTE: the CONTRACT's giftConfig payload (task text, quoted verbatim in
// giftConstants.js's header) has no `basis` field — "basis" (always
// "discounted subtotal") is a fixed, non-configurable design decision; the
// instruction is to surface it only as help text (GiftQualifyingCard), never
// as a field or control, and this adaptor follows that literally by never
// sending one either. The OLDER gift-engine-spec-shahram.md §2 DTO example
// does show a `"basis":"discounted_subtotal"` key and an `"eligibility"`
// key inside giftConfig — both deliberately omitted here because the task's
// CONTRACT text (given to the backend/Rust agents too) is the one to match
// exactly. Flagged in the final report for reconciliation if the backend's
// real DTO disagrees.
export function giftConfigToDto(cfg) {
  if (!cfg) return null;
  return {
    title: cfg.title || '',
    scope: giftScopeToDto(cfg.scope),
    oncePerOrder: cfg.oncePerOrder !== false,
    tiers: (cfg.tiers ?? []).map(giftTierToDto),
    display: GIFT_DISPLAY_MODES.includes(cfg.display) ? cfg.display : 'BAR',
  };
}

export const calcDiscountType = (hasBulkDiscount) => {
  switch (hasBulkDiscount) {
    case campaignTypes.General:
      return serverCampaignTypes.General;
    case campaignTypes.Volume:
      return serverCampaignTypes.Volume;
    case campaignTypes.Order:
      return serverCampaignTypes.Order;
    case campaignTypes.BOGO:
      return serverCampaignTypes.BOGO;
    case campaignTypes.Shipping:
      return serverCampaignTypes.Shipping;
    case campaignTypes.Bundle:
      return serverCampaignTypes.Bundle;
    case campaignTypes.CrossSell:
      return serverCampaignTypes.CrossSell;
    case campaignTypes.Wholesale:
      return serverCampaignTypes.Wholesale;
    case campaignTypes.Dropshipper:
      return serverCampaignTypes.Dropshipper;
    case campaignTypes.FreeGift:
      return serverCampaignTypes.FreeGift;
    case campaignTypes.Upsell:
      return serverCampaignTypes.Upsell;
    default:
      break;
  }
};

/**
 * Minimal, defensive scope-only adaptor for the campaign-precedence coverage
 * preview (POST /campaigns/coverage). Deliberately does NOT reuse
 * saveCampaignAdaptor: that function assumes every auction* array is already
 * initialized and calls .map() on all of them unconditionally regardless of
 * which scope type is actually active (plus a large amount of pricing/
 * discount-type logic the coverage endpoint never reads) — a real,
 * reproduced crash mid-edit (picking a new product tripped a field this
 * preview doesn't even need). Every array here defaults to [] instead of
 * throwing, so a still-settling form state degrades to "empty scope" rather
 * than silently killing the debounced preview with no visible error.
 */
// Set layer (Bundle / Cross-sell / Upsell / Free gift): a set campaign competes with
// campaigns of its own type for products, and its scope (trigger AND reward pools) lives
// in the builder config rather than in the generic scope fields. Converted with the same
// functions the save uses, defensively: a half-filled builder degrades to "no scope yet".
function safeConfigDto(toDto, cfg) {
  if (!cfg) return undefined;
  try {
    return toDto(cfg) ?? undefined;
  } catch (e) {
    return undefined;
  }
}

export function sendCoveragePreviewAdaptor(campaign) {
  return {
    bundleConfig: safeConfigDto(bundleConfigToDto, campaign?.bundleConfig),
    xsellConfig: safeConfigDto(xsellConfigToDto, campaign?.xsellConfig),
    giftConfig: safeConfigDto(giftConfigToDto, campaign?.giftConfig),
    upsellConfig: safeConfigDto(upsellConfigToDto, campaign?.upsellConfig),
    id: campaign?.id,
    name: campaign?.name ?? '',
    type: calcDiscountType(campaign?.hasBulkDiscount) ?? 'general',
    productInclusionType: productSelection[campaign?.auctionTypeProductSelection] ?? 'all_store',
    productExclusionType: productExclude[campaign?.auctionTypeProductExclude] ?? 'none',
    collections: (campaign?.auctionCollections ?? []).map((c) => ({
      collectionId: parseInt(c.collectionId),
      isExcluded: c.isExclude,
      meta: { title: c.title || c.collection?.title },
    })),
    vendors: (campaign?.auctionVendors ?? []).map((t) => ({
      vendorName: t.vendorId,
      isExcluded: t.isExclude,
    })),
    productTypes: (campaign?.auctionProductTypes ?? []).map((t) => ({
      productTypeName: t.productTypeName,
      isExcluded: t.isExclude,
    })),
    products: mapProducts(normalizeProductRows(campaign?.auctionProducts ?? [])),
    tags: (campaign?.auctionTags ?? []).map((t) => ({ tagName: t.tagName, isExcluded: t.isExclude })),
    priceRanges: (campaign?.auctionPriceRanges ?? []).map((t) => ({
      fromPrice: parseFloat(t.fromPrice),
      toPrice: parseFloat(t.toPrice),
      isExcluded: t.isExclude,
    })),
    variantMetafieldRules: (campaign?.auctionVariantMetafieldRules ?? []).map((r) => ({
      namespace: r.namespace,
      key: r.key,
      name: r.name,
      type: r.type,
      operator: r.operator,
      value: r.value != null ? String(r.value) : '',
    })),
    variantMetafieldMatchType: campaign?.variantMetafieldMatchType ?? 'all',
    // BOGO ("Customer buys") scope — gettingProducts() branches to
    // processingBogo() for type === buy_one_get_one, which reads these
    // bxgy* fields directly instead of productInclusionType/collections/etc
    // above. Harmless no-op for every other campaign type (all undefined/[]).
    // The BOGO editor has no top-level auctionTypeProductSelection etc. to
    // read this from — its own live "Customer buys" state lives in a
    // sibling React state var, not on `campaign` — so whichever call site
    // wires ScopeAllocationPanel into BxgyCustomerBuysSection is expected to
    // pass a campaign-shaped object with these already merged in.
    bxgyAppliesToType: campaign?.bxgyAppliesToType,
    bxgyCollectionIds: campaign?.bxgyCollectionIds ?? [],
    bxgyTags: campaign?.bxgyTags ?? [],
    bxgyVendors: campaign?.bxgyVendors ?? [],
    bxgyBuyProducts: campaign?.bxgyBuyProducts ?? [],
    bxgyExclusionsEnabled: campaign?.bxgyExclusionsEnabled ?? false,
    bxgyActiveExclusionType: campaign?.bxgyActiveExclusionType,
    bxgyExclusions: campaign?.bxgyExclusions ?? null,
    // Lets the backend exempt a combining BOGO draft from arbitration.
    bxgyCombineWithOthers: campaign?.bxgyCombineWithOthers ?? false,
    // Cost-based Wholesale / Dropshipper: lets the product list flag variants a
    // 'skip' missing-cost rule will leave undiscounted (same defaults as the save).
    wholesaleOnMargin: !!campaign?.wholesaleOnMargin,
    wholesaleMissingCostRule: campaign?.wholesaleMissingCostRule || 'skip',
    dropshipperPricingBase: campaign?.dropshipperPricingBase ?? 1,
  };
}

export function saveCampaignAdaptor(campaign) {
  if (campaign.hasBulkDiscount === campaignTypes.Order) {
    campaign.auctionTypeProductSelection = 0;
    campaign.auctionTypeProductExclude = 0;
  }

  const newPriceDiscountTypeEnum = ['percentage', 'fix_price', 'new_price'];
  const newPriceChangeTypeEnum = ['increase', 'decrease'];

  // Callers may pass these as a numeric index OR as the already-resolved enum string
  // (CampaignForm pre-converts to a string). Normalize both to the backend enum string.
  const resolveEnum = (enumArr, val) => {
    if (typeof val === 'string') return enumArr.includes(val) ? val : null;
    return enumArr[val] ?? null;
  };

  // A General campaign is a Bulk Price Update (new_price) only when its discount
  // TYPE actually says so — NOT merely because a generalDiscountValue is present.
  // The grid/list adaptor sends generalDiscountValue for Flat Product campaigns too
  // (it mirrors generalDiscountValue → newPrice/discountPercentage), so the old
  // `generalDiscountValue != null` test silently flipped a Flat Product Discount into
  // a Bulk Price Update every time the campaign was re-saved through this adaptor
  // (e.g. force-starting a conflicting campaign → updateAndActivateCampaignForceConflict).
  // Decide from the explicit discount type instead: `auctionType` when present (set by
  // list.adaptor — new_price→3, percentage→1, fix_price→2), else the form's
  // `generalDiscountType` string, else fall back to the legacy value check.
  const isGeneralNewPriceType =
    campaign.auctionType != null
      ? generalDiscountType[campaign.auctionType] === 'new_price'
      : campaign.generalDiscountType === 'new_price' || campaign.generalDiscountValue != null;
  const useNewPriceFields =
    (campaign.hasBulkDiscount === campaignTypes.General ||
      campaign.hasBulkDiscount === campaignTypes.Dropshipper) && isGeneralNewPriceType &&
    campaign.generalDiscountValue != null;
    //campaign.hasBulkDiscount === campaignTypes.General && isGeneralNewPriceType;

  // Price floor applies to any General campaign (Flat Discount + Bulk Price Update),
  // to Dropshipper (selling-price base) and to Wholesale when it runs on selling price.
  const usePriceProtection =
    campaign.hasBulkDiscount === campaignTypes.General ||
    campaign.hasBulkDiscount === campaignTypes.Dropshipper ||
    campaign.hasBulkDiscount === campaignTypes.Wholesale;

  const data = {
    isImmediately: campaign.isImmediately,
    name: campaign.name,
    type: calcDiscountType(campaign.hasBulkDiscount),
    productInclusionType: productSelection[campaign.auctionTypeProductSelection],
    productExclusionType: productExclude[campaign.auctionTypeProductExclude],
    generalDiscountType: useNewPriceFields
      ? 'new_price'
      : generalDiscountType[campaign.auctionType],
    generalDiscountValue: useNewPriceFields
      ? Number(campaign.generalDiscountValue)
      : (parseFloat(campaign.newPrice) || 0),
    generalDiscountPriceChangeMode: generalDiscountMode[campaign.auctionDiscountType],
    generalDiscountHandleCompareAtPrice: campaign.handleCompareAtPrice,
    // Cents/round source differs per flow: New Price uses generalDiscountCustomCent
    // (+ its own toggle); Wholesale's "Adjust Cents" writes roundDigit (e.g. '99'),
    // so forward THAT — otherwise the cents never reach the backend (which only
    // reads generalDiscountCustomCent) and the .99 rounding silently no-ops.
    generalDiscountCustomCent:
      campaign.hasBulkDiscount === campaignTypes.Wholesale
        ? (parseFloat(campaign.roundDigit) / 100 || null)
        : useNewPriceFields
        ? (campaign.generalDiscountHasAdjustCents && campaign.generalDiscountCustomCent != null
            ? Number(campaign.generalDiscountCustomCent)
            : null)
        : (parseFloat(campaign.roundDigit) / 100 || null),
    newPriceDiscountType: useNewPriceFields
      ? resolveEnum(newPriceDiscountTypeEnum, campaign.newPriceDiscountType)
      : null,
    newPriceChangeType: useNewPriceFields
      ? resolveEnum(newPriceChangeTypeEnum, campaign.newPriceChangeType)
      : null,
    hasPriceProtection: usePriceProtection ? !!campaign.hasPriceProtection : false,
    PriceFloor: usePriceProtection
      ? (campaign.hasPriceProtection && campaign.generalDiscountPriceFloor != null
          ? Number(campaign.generalDiscountPriceFloor)
          : 0)
      : 0,
    volumeDiscountMinimumType: volumeMinType[campaign.quantityDiscountMinType],
    volumeDiscountMinimumApplyType: volumeMinApplyType[campaign.quantityDiscountMinApplyType],
    volumeDiscountIsOncePerOrder: campaign.isApplyOncePerOrder,
    autoUpdateIncluded: campaign.includeOnFly,
    autoUpdateExcluded: campaign.excludeOnFly,
    isOrderDiscountCombined: campaign.isOrderDiscountCombined,
    orderExecutionOrder: campaign.orderExecutionOrder,
    hasProductCountdown: campaign.hasProductCountdown,
    activeByDiscountCode: campaign.activeByDiscountCode,
    discountCode: campaign.discountCode,
    discountCodeId: campaign.discountCodeId,
    discountCodeAppliesOncePerCustomer: campaign.discountCodeAppliesOncePerCustomer,
    // Discount-code method (single shared code vs bulk unique codes). Only sent
    // when the form actually set them (undefined fields are dropped server-side).
    codeMethod: campaign.codeMethod,
    codeType: campaign.codeType,
    usageLimitPerCode: campaign.usageLimitPerCode,
    perCustomerLimit: campaign.perCustomerLimit,
    codeRandomLength: campaign.codeRandomLength,
    codeCount: campaign.codeCount,
    codePrefix: campaign.codePrefix,
    // Null-safe on quantityDiscountRules itself (not just its [0] entry):
    // Free Gift never populates it (not in makeDiscountRules'/
    // readyDiscountRulesToSave's qtyDiscounts list the way Bundle/Cross-
    // sell/Upsell/BOGO are — Gift's discount lives entirely in giftConfig),
    // so it arrives here `undefined`, not `[]`, and `undefined[0]` crashed
    // EVERY Free Gift save with "Cannot read properties of undefined
    // (reading '0')" before saveCampaignAdaptor ever reached the network —
    // reported as "nothing is sent to the server".
    volumeDiscountType:
      quantityDiscountType[campaign.quantityDiscountRules?.[0]?.discountType] ||
      generalDiscountType[campaign.auctionType],
    startAt: campaign.startDate,
    endAt: campaign.endDate,
    tiers:
      campaign.hasBulkDiscount === campaignTypes.BOGO ||
      campaign.hasBulkDiscount === campaignTypes.Shipping ||
      campaign.hasBulkDiscount === campaignTypes.Bundle ||
      campaign.hasBulkDiscount === campaignTypes.CrossSell ||
      campaign.hasBulkDiscount === campaignTypes.Upsell
        ? campaign.quantityDiscountRules
        : campaign.quantityDiscountRules?.map((x) => ({
            discountValue: parseFloat(x.discountValue),
            minimumValue: parseFloat(x.minValue),
            maximumValue: parseFloat(x.maxValue),
          })),
    collections: campaign.auctionCollections.map((c) => ({
      collectionId: parseInt(c.collectionId),
      isExcluded: c.isExclude,
      meta: { title: c.title || c.collection?.title },
    })),
    vendors: campaign.auctionVendors.map((t) => ({
      vendorName: t.vendorId,
      isExcluded: t.isExclude,
    })),
    productTypes: campaign.auctionProductTypes.map((t) => ({
      productTypeName: t.productTypeName,
      isExcluded: t.isExclude,
    })),
    products: mapProducts(normalizeProductRows(campaign.auctionProducts)),
    tags: campaign.auctionTags.map((t) => ({ tagName: t.tagName, isExcluded: t.isExclude })),
    priceRanges: campaign.auctionPriceRanges.map((t) => ({
      fromPrice: parseFloat(t.fromPrice),
      toPrice: parseFloat(t.toPrice),
      isExcluded: t.isExclude,
    })),
    variantMetafieldRules: (campaign.auctionVariantMetafieldRules ?? []).map((r) => ({
      namespace: r.namespace,
      key: r.key,
      name: r.name,
      type: r.type,
      operator: r.operator,
      value: r.value != null ? String(r.value) : '',
    })),
    variantMetafieldMatchType: campaign.variantMetafieldMatchType ?? 'all',
    // Gate eligibility strictly by the selected type so stale lists from a previous
    // selection never leak through. "all" → isCustomerIncluded=false + empty lists
    // (open to everyone, incl. guests). "logged_in" → gated on auth alone (empty lists,
    // the function enforces the guest cutoff). segment/specific/tag → only that list.
    isCustomerIncluded:
      campaign.auctionTypeCustomerSelection === 'logged_in' ||
      (campaign.auctionTypeCustomerSelection === 'segment' && campaign.customerSegment != undefined && campaign.customerSegment.length > 0) ||
      (campaign.auctionTypeCustomerSelection === 'tag' && campaign.customerTags != undefined && campaign.customerTags.length > 0) ||
      (campaign.auctionTypeCustomerSelection === 'specific' && campaign.customers != undefined && campaign.customers.length > 0),
    CustomersTags: campaign.auctionTypeCustomerSelection === 'tag' && campaign.customerTags != undefined ? campaign.customerTags.map((t) => t.id) : [],
    CustomerSegment: campaign.auctionTypeCustomerSelection === 'segment' && campaign.customerSegment != undefined ? campaign.customerSegment : [],
    Customers: campaign.auctionTypeCustomerSelection === 'specific' && campaign.customers != undefined ? campaign.customers : [],
    auctionTypeCustomerSelection: campaign.auctionTypeCustomerSelection,
    A_b_tiers: campaign?.abDiscountRules?.length ? campaign.abDiscountRules : [],
    widgetStyle:
      campaign.hasBulkDiscount === campaignTypes.BOGO
        ? campaign.widgetStyle
        : campaignWidgetStyle.none,
    isShowOnProductPage: campaign?.isShowOnProductPage || false,
    isShowOnCartPage: campaign?.isShowOnCartPage || false,
    isShowWidgetOnThisCampaign: campaign?.isShowWidgetOnThisCampaign || false,
    hasSalesBadge: campaign?.hasSalesBadge || false,
    salesBadgeText: campaign?.salesBadgeText || 'Special Offer',
    salesBadgeTextColor: campaign?.salesBadgeTextColor || '#FFFFFF',
    salesBadgeBackgroundColor: campaign?.salesBadgeBackgroundColor || '#FF0000',
    salesBadgeType: campaign?.salesBadgeType || 'circle',
    salesBadgePosition: campaign?.salesBadgePosition || 'top_left',
    salesBadgeHasBorder: campaign?.salesBadgeHasBorder || false,
    salesBadgeTranslations: campaign?.salesBadgeTranslations || undefined,
    // ── Wholesale / B2B Pricing (margin knobs + progress-bar config) ──
    ...(campaign.hasBulkDiscount === campaignTypes.Wholesale
      ? {
          wholesaleOnMargin: !!campaign.wholesaleOnMargin,
          wholesaleMinProfitType: campaign.wholesaleMinProfitType || 'fixed',
          wholesaleMissingCostRule: campaign.wholesaleMissingCostRule || 'skip',
          wholesaleOnlyEligible: campaign.wholesaleOnlyEligible ?? true,
          wholesaleProgressBar: campaign.wholesaleProgressBar ?? null,
          hasProfitProtection: !!campaign.hasProfitProtection,
          minMarginForProfit:
            campaign.minMarginForProfit != null ? Number(campaign.minMarginForProfit) : null,
        }
      : {}),
    // ── Dropshipper Pricing (cost-based "Based on" + profit floor) ──
    ...(campaign.hasBulkDiscount === campaignTypes.Dropshipper
      ? {
          dropshipperPricingBase: campaign.dropshipperPricingBase ?? 1,
          hasProfitProtection: !!campaign.hasProfitProtection,
          minMarginForProfit:
            campaign.minMarginForProfit != null ? Number(campaign.minMarginForProfit) : null,
          wholesaleMinProfitType: campaign.wholesaleMinProfitType || 'fixed',
          wholesaleMissingCostRule: campaign.wholesaleMissingCostRule || 'skip',
        }
      : {}),
    forceConflictStart: Boolean(campaign.forceConflictStart),
    conflictManagementTempModel: campaign?.conflictManagementTempModel,
    pendingTakeovers: campaign?.pendingTakeovers,
    saveAsDraft: campaign?.saveAsDraft === true,
    // Arbitration tiebreak (Pro-gated server-side, see enforcePriorityPermission
    // in Prime-Backend). Sent as-is; the backend coerces to 0 on unqualified plans.
    bogoConfig: campaign?.bogoConfig
      ? (() => {
          const { offerType, customerBuys, customerGets, combination, storefrontDisplay } = campaign.bogoConfig;
          const buys = customerBuys ?? {};
          const gets = customerGets ?? {};
          const combo = combination ?? {};
          const display = storefrontDisplay ?? {};
          return {
            // Offer type is just a UI preset selector — when user skips selection,
            // fall back to CUSTOM_RULE so the backend always receives a valid enum value.
            offerType: offerType ?? 'CUSTOM_RULE',
            minimumQuantity: Number(buys.minimumQuantity) || 1,
            appliesToType: buys.appliesToType,
            productIds: buys.appliesToSelection?.productIds ?? [],
            collectionIds: buys.appliesToSelection?.collectionIds ?? [],
            tags: buys.appliesToSelection?.tags ?? [],
            vendors: buys.appliesToSelection?.vendors ?? [],
            buyProducts: mapProducts(normalizeProductRows(buys.auctionProducts)),
            buyCollections: (buys.auctionCollections ?? []).map((c) => ({
              collectionId: parseInt(c.collectionId),
              isExcluded: c.isExclude,
              meta: { title: c.title || c.collection?.title || '' },
            })),
            itemCountMode: buys.itemCountMode === 'ANY_ELIGIBLE_ITEMS' ? 'ACROSS_ALL_ITEMS' : (buys.itemCountMode ?? 'SAME_ITEM_ONLY'),
            minimumSpend: buys.minimumSpend !== '' && buys.minimumSpend != null ? Number(buys.minimumSpend) : null,
            exclusionsEnabled: Boolean(buys.exclusionsEnabled),
            activeExclusionType: buys.exclusionsEnabled ? buys.activeExclusionType : null,
            exclusions: buys.exclusionsEnabled
              ? {
                  ...(buys.exclusions ?? {}),
                  excludeProducts: mapProducts(normalizeProductRows(buys.excludeAuctionProducts)),
                  excludeCollections: (buys.excludeAuctionCollections ?? []).map((c) => ({
                    collectionId: parseInt(c.collectionId),
                    isExcluded: c.isExclude,
                    meta: { title: c.title || c.collection?.title || '' },
                  })),
                }
              : null,
            rewardType: gets.rewardType,
            rewardAppliesTo: gets.rewardAppliesTo,
            rewardPercentage: gets.rewardValue?.percentage !== '' && gets.rewardValue?.percentage != null ? Number(gets.rewardValue.percentage) : null,
            rewardFixedAmount: gets.rewardValue?.fixedAmount !== '' && gets.rewardValue?.fixedAmount != null ? Number(gets.rewardValue.fixedAmount) : null,
            rewardProductIds: gets.rewardSelection?.productIds
              ?? gets.rewardProducts?.flatMap((p) => p.variantIdList ?? p.variantIds ?? [])
              ?? [],
            rewardCollectionIds: gets.rewardSelection?.collectionIds ?? [],
            rewardProducts: mapProducts(normalizeProductRows(gets.rewardProducts)),
            rewardCollections: (gets.rewardCollections ?? []).map((c) => ({
              collectionId: parseInt(c.collectionId),
              isExcluded: c.isExclude,
              meta: { title: c.title || c.collection?.title || '' },
            })),
            discountedItemQuantity: Number(gets.discountedItemQuantity) || 1,
            applicationMode: gets.applicationMode,
            maximumRewardsPerOrder: gets.maximumRewardsPerOrder !== '' && gets.maximumRewardsPerOrder != null ? Number(gets.maximumRewardsPerOrder) : null,
            combineWithOtherDiscounts: Boolean(combo.combineWithOtherDiscounts),
            executionOrder: combo.executionOrder,
            storefrontDisplayType: display.storefrontDisplayType,
            bogoNote: campaign.bogoConfig.bogoNote ?? null,
          };
        })()
      : null,
    // ── Product Bundle (set config + profit protection on the campaign) ──
    ...(campaign.hasBulkDiscount === campaignTypes.Bundle
      ? {
          bundleConfig: bundleConfigToDto(campaign.bundleConfig),
          hasProfitProtection: !!campaign.hasProfitProtection,
          minMarginForProfit:
            campaign.hasProfitProtection &&
            campaign.minMarginForProfit != null &&
            campaign.minMarginForProfit !== ''
              ? Number(campaign.minMarginForProfit)
              : null,
          // Bundles only protect a margin percentage (plan §4.2).
          wholesaleMinProfitType: 'percent',
          wholesaleMissingCostRule: campaign.wholesaleMissingCostRule || 'skip',
          bellowMarginAction:
            campaign.bellowMarginAction === 'noAction' ? 'noAction' : 'reduceRewards',
        }
      : {}),
    // ── Cross-sell (trigger + offer + reward config) ──
    ...(campaign.hasBulkDiscount === campaignTypes.CrossSell
      ? {
          xsellConfig: xsellConfigToDto(campaign.xsellConfig),
        }
      : {}),
    // ── Free Gift (scope + tiers + display config) ──
    ...(campaign.hasBulkDiscount === campaignTypes.FreeGift
      ? {
          giftConfig: giftConfigToDto(campaign.giftConfig),
        }
      : {}),
    // ── Upsell (trigger + ladder/offer + incentive + phrasing config) ──
    ...(campaign.hasBulkDiscount === campaignTypes.Upsell
      ? {
          upsellConfig: upsellConfigToDto(campaign.upsellConfig),
        }
      : {}),
    shippingTitle: campaign?.shippingTitle,
    notAllowMoreThanProductPrice: campaign?.shippingNoLargerThanProductPrice ?? false,
    convertLargerToFreeShip: campaign?.shippingConvertToFreeShipping ?? false,
    hasPurchaseHistoryCondition: campaign?.hasPurchaseHistoryCondition ?? false,
    purchaseHistory: campaign?.purchaseHistory ?? [],
    isAllMarkets: campaign?.isAllMarkets ?? true,
    selectedMarkets: campaign?.selectedMarkets ?? [],
    limitFreeShipping: campaign?.freeShippingConditions?.freeShippingLimitEnabled ?? false,
    maxShippingRateCovered: campaign?.freeShippingConditions?.freeShippingMaxRate !== '' ? parseFloat(campaign?.freeShippingConditions?.freeShippingMaxRate) || null : null,
    exceedLimitAction: campaign?.freeShippingConditions?.freeShippingExceedAction ?? 'do_not_apply',
    excludeRemoteZones: campaign?.freeShippingConditions?.freeShippingExcludeRemoteZones ?? false,
    shippingCombineWithOtherDiscounts: campaign?.shippingCombination?.combineWithOtherDiscounts ?? false,
    shippingAllowProductDiscounts:     campaign?.shippingCombination?.allowProductDiscounts ?? true,
    shippingAllowOrderDiscounts:       campaign?.shippingCombination?.allowOrderDiscounts ?? false,
    shippingExecutionOrder:            campaign?.shippingCombination?.executionOrder ?? 'before',
    shippingOfferType:                 campaign?.shippingOfferType ?? null,
    templateId:                        campaign?.templateId ?? null,
  };
  return data;
}

